import { getOrCreateMainLogger } from "../logger/main";
import { appDataCachePath, appDataPreloadsPath } from "./appDataFolder";
import { isValidURL, deleteFolderRecursive } from "../util";
import { Manifest } from "../types/types";
import { mainStore } from "./mainStore";
import { StandardResponse, WebContentsId, Preload } from "../types/types";
import { mkdirp } from "mkdirp";
import fs from "fs";

const logger = getOrCreateMainLogger();

// @deprecation - Flag this deprecation so that it can be grepped more easily
/**
 * Returns a warning saying that we will deprecate this functionality very soon.
 *
 * @param {*} url
 * @returns
 */
const getFilenameTrustedPreloadDeprecationWarning = function (url: string) {
	return `Deprecation: URL "${url}" is not a valid URL. Prior
	 to version 3.9.0 filenames were allowed for trusted preloads. In version 4.0, we will only allow absolute URLs for trusted preloads. Please change your url "${url}".`;
};

/**
 * make required preload directories
 */
export const makePreloadDirectories = () => {
	const cachePath = appDataCachePath();
	if (!fs.existsSync(cachePath)) {
		logger.log(`INITIAL SETUP: Creating preload directory:${cachePath}`);
		mkdirp.sync(cachePath);
	}

	const remoteModuleFolderPath = appDataPreloadsPath();
	if (!fs.existsSync(remoteModuleFolderPath)) {
		logger.log(`INITIAL SETUP: Creating remote module directory:${cachePath}`);
		mkdirp.sync(remoteModuleFolderPath);
	}
};

/**
 * Returns the extension for a url if one exists, otherwise returns ""
 */
const getUrlExtension = (url: string): string => {
	const maybeExtension = url.split(/[#?]/)[0].split(".").pop()?.trim();
	return maybeExtension === url ? "" : (maybeExtension ?? "");
};

/**
 * Returns a window title bar url given a url entry that might be either a directory
 * or an actual file. The requested extension, either js or html, is added so that this
 * can be used either as a browserview or injected titlebar. If the provided url is a
 * directory then index.js/index.html is assumed.
 *
 * @param entry Url of where to find the window title bar
 * @param extension Either "js" or "html"
 * @returns A definitive url
 */
export const calculateWindowTitleBarUrl = (url: string, extension: "js" | "html"): string => {
	// Get the file extension if one exists
	const existingExtension = getUrlExtension(url);
	// If extension exists, replace with .js or .html
	if (existingExtension) return `${url.substr(0, url.lastIndexOf("."))}.${extension}`;
	// Ensure trailing slash
	const entryWithSlash = url.replace(/\/?$/, "/");
	// Append index.js or index.html
	return `${entryWithSlash}index.${extension}`;
};

/**
 * Gets the source code for all the preloads that should be loaded for the given WebConents ID.
 */
export const getPreloads = (webContentsId: WebContentsId): StandardResponse<Preload[]> => {
	const record = mainStore.webContents[webContentsId.toString()];
	if (!record) {
		const err = `getPreloads() No record found for webContents id ${webContentsId}`;
		logger.error(err);
		return { err };
	}
	if (!record.preloads) {
		const err = `getPreloads() Invariant: preloads entry is empty for record ${record.windowName}`;
		logger.error(err, record);
		return { err };
	}

	const preloads = record.preloads.map((preload) => {
		if (preload.fileName) {
			return {
				...preload,
				src: fs.readFileSync(preload.fileName, { encoding: "utf8" }),
			};
		} else {
			return preload;
		}
	});

	return { data: preloads };
};

/**
 * Warns user if they have provided a filename as a trusted preload in their config.
 *
 * @memberof Main
 */
export const validateTrustedPreloadArray = (manifest: Manifest) => {
	logger.debug("Main->validateTrustedPreloadArray");
	// We should pull finsemble out of this.manifest.finsemble and remove the first check.
	// this.manifest.finsemble should always exist..
	if (manifest.finsemble && Array.isArray(manifest.finsemble.trustedPreloads)) {
		manifest.finsemble.trustedPreloads.forEach((preload: any) => {
			if (!isValidURL(preload)) {
				// this makes it red.
				logger.warn(getFilenameTrustedPreloadDeprecationWarning(preload));
			}
		});
	}
};

/**
 * Can only be used in the main process.
 */
export const clearPreloadCache = async () => {
	logger.debug("helpers->clearPreloadCache");
	// If the require is moved outside of the function, it will cause an error.
	// getCachePath calls getAppDataFolder, which assumes it's being run in the main proc.
	const { appDataFolder } = require("../main/appDataFolder");
	// Its fair to wrap the deleteFolderRecursive() call with a try/catch
	// block because it uses fs module a lot while any of fs methods could throw
	try {
		deleteFolderRecursive(appDataFolder);
	} catch (err) {
		logger.error(`Failed to delete preload cache ${appDataFolder}. err.message`);
	}
};

export const initializePreloads = async (manifest: Manifest) => {
	// delete preload cache when electron starts
	clearPreloadCache();
	validateTrustedPreloadArray(manifest);
	makePreloadDirectories();
};
